import type { HomeRow, HomeRowKind } from './home.js';

/**
 * Quali fasce della home si mostrano sul telefono.
 *
 * ## Una tabella esaustiva, e non un filtro
 *
 * `Record<HomeRowKind, boolean>` obbliga a nominare **ogni** tipo: aggiungendone uno alla union
 * senza decidere, questo file **non compila**. È la differenza che conta, perché le due alternative
 * sbagliano in silenzio in due direzioni opposte:
 *
 * - con un elenco di quelli ammessi, una fascia nuova **sparisce** dal telefono e nessuno se ne
 *   accorge — la si vede sulla TV, si dà per fatta, e sul telefono non c'è mai stata;
 * - con un elenco di quelli esclusi, una fascia nuova **compare** senza che nessuno l'abbia disegnata
 *   per 390 px, e si scopre guardando uno schermo.
 *
 * Qui il compilatore chiede, e chi aggiunge risponde.
 *
 * ## Perché `cats` non c'è
 *
 * Le categorie portano a sfogliare i canali, e sul telefono quel gesto ha già un posto: le etichette
 * in cima alla Guida. Una fascia orizzontale di categorie nella home sarebbe una seconda porta per la
 * stessa stanza, che su uno schermo dove tutto si scorre col pollice costa una schermata intera.
 *
 * Lo dice `docs/04-SCHERMATE-MOBILE.md` §1: nove tipi sulla TV, sette sul telefono — spariscono
 * `cats` e `apps`. `apps` non è in questa union perché non è ancora implementato da nessuna parte;
 * quando arriverà, il compilatore lo chiederà qui.
 */
const SHOWN_ON_PHONE: Readonly<Record<HomeRowKind, boolean>> = {
  continue: true,
  recs: true,
  latest: true,
  picks: true,
  onair: true,
  aggs: true,
  events: true,
  // L'unica esclusa: il suo gesto vive nelle etichette della Guida.
  cats: false,
};

/**
 * Le fasce da disegnare sul telefono, nell'ordine in cui arrivano.
 *
 * L'ordine non si tocca: lo decide la vetrina, che è governabile dal pannello, e riordinare qui
 * vorrebbe dire che l'operatore sposta una fascia e sul telefono non si muove niente.
 */
export function phoneHomeRows(rows: ReadonlyArray<HomeRow>): HomeRow[] {
  return rows.filter((row) => SHOWN_ON_PHONE[row.kind]);
}
